import { useEffect, useMemo, useRef } from 'react'
import { Box, Stack } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import ExportButton from 'components/ExportButton'
import Filters from 'components/Filters'
import Header from 'components/Header'
import { useSession } from 'components/Providers'
import { getGradeLevelAndSections, getNurseConditionTypes } from 'store/lookup'
import { searchCondition, setConditionQuery } from 'store/nurse/nurse-report'
import HealthReportList from './List'
import useHealthReportData from './useHealthReportData'

export default function HealthReportPage() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { district, academicYear } = useSession()
  const gradeLevelAndSections = useSelector(state => state.lookup.gradeLevelAndSections)
  const conditionTypes = useSelector(state => state.lookup.nurse.conditionType)
  const query = useSelector(state => state.nurse.report.conditionQuery)
  const { rows, columns, loading } = useHealthReportData()

  useEffect(() => {
    gradeLevelAndSections || (academicYear && dispatch(getGradeLevelAndSections()))
  }, [dispatch, academicYear, Boolean(gradeLevelAndSections)])

  useEffect(() => {
    conditionTypes || dispatch(getNurseConditionTypes())
  }, [dispatch, Boolean(conditionTypes)])

  const scope = `${district?.id ?? ''}:${academicYear?.id ?? ''}`
  const lastScope = useRef(scope)
  useEffect(() => {
    const changed = lastScope.current !== scope
    lastScope.current = scope
    const next = changed ? {} : query
    if (changed) dispatch(setConditionQuery(next))
    dispatch(searchCondition(next))
  }, [dispatch, scope])

  const gradeOptions = useMemo(() =>
    gradeLevelAndSections?.flatMap(el => [
      { value: `L:${el.id}`, label: `${t('Grade')} ${el.id}`, gradeLevelId: el.id, gradeSectionId: null },
      ...el.section.map(s => ({
        value: `S:${s.id}`,
        label: `${t('Section')} ${s.gradeSection}`,
        gradeLevelId: el.id,
        gradeSectionId: s.id,
      })),
    ]) ?? [],
    [gradeLevelAndSections, t]
  )

  let gradeValue = ''
  if (query.gradeSectionId) gradeValue = `S:${query.gradeSectionId}`
  else if (query.gradeLevelId) gradeValue = `L:${query.gradeLevelId}`

  const conditionTypeOptions = useMemo(
    () => conditionTypes?.map(type => ({ value: type.id, label: type.name })) ?? [],
    [conditionTypes],
  )

  const filtersConfig = [
    {
      key: 'grade',
      label: t('Grade Level / Section'),
      type: 'autocomplete',
      gridProps: { xs: 12, sm: 6, md: 4 },
      renderOption: ({ style, ...props }, option) => (
        <li {...props} key={option.value} style={{ ...style, paddingLeft: option.gradeSectionId ? 40 : 16 }}>
          {option.label}
        </li>
      ),
    },
    {
      key: 'type',
      label: t('Condition Type'),
      type: 'autocomplete',
      gridProps: { xs: 12, sm: 6, md: 4 },
    },
  ]

  const handleChange = newQuery => {
    const next = { ...newQuery }
    const grade = next.grade
    delete next.grade
    delete next.gradeLevelId
    delete next.gradeSectionId
    if (grade?.startsWith('S:')) next.gradeSectionId = Number(grade.slice(2))
    else if (grade?.startsWith('L:')) next.gradeLevelId = grade.slice(2)
    dispatch(setConditionQuery(next))
    dispatch(searchCondition(next))
  }

  return (
    <Box>
      <Header title={t('Health Report')} small sx={{ paddingTop: 2 }} />
      <Stack direction="row" alignItems="center" spacing={2} sx={{ my: 1 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Filters
            filtersConfig={filtersConfig}
            values={{ ...query, grade: gradeValue }}
            onChange={handleChange}
            optionsMap={{
              grade: gradeOptions,
              type: conditionTypeOptions,
            }}
            gridContainerProps={{ spacing: 2, sx: { p: 0 } }}
          />
        </Box>
        <ExportButton
          columns={columns}
          rows={rows}
          fileName="health-report"
          sheetName={t('Health Report')}
          sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
        />
      </Stack>
      <HealthReportList rows={rows} columns={columns} loading={loading} />
    </Box>
  )
}
